import { Icon } from '@iconify/react'
import {
  calculateDelta,
  formatCurrency,
  formatDeltaPercentMagnitude,
  formatNumber,
} from '@/lib/campaign-dashboard'
import type { CampaignDashboardSummary } from '@/types/campaign-report'
import styles from './CampaignDashboard.module.css'

interface CampaignDashboardKpisProps {
  summary: CampaignDashboardSummary
  previousSummary: CampaignDashboardSummary | null
}

type KpiConfig = {
  label: string
  icon: string
  value: number | null
  formatter: (value: number | null) => string
  previousValue: number | null
}

function renderComparison(current: number | null, previous: number | null) {
  if (current === null) return null
  const comparison = calculateDelta(current, previous)
  const magnitudeLabel = formatDeltaPercentMagnitude(comparison)
  if (magnitudeLabel === null || comparison.isPositive === null) {
    return <span className={styles.comparisonNeutral}>Sem comparativo</span>
  }

  return (
    <span
      className={
        comparison.isPositive ? styles.comparisonPositive : styles.comparisonNegative
      }
    >
      <Icon icon={comparison.isPositive ? 'mdi:trending-up' : 'mdi:trending-down'} />
      {magnitudeLabel} vs periodo anterior
    </span>
  )
}

export function CampaignDashboardKpis({
  summary,
  previousSummary,
}: CampaignDashboardKpisProps) {
  const kpis: KpiConfig[] = [
    {
      label: 'Receita total',
      icon: 'mdi:cash-multiple',
      value: summary.total_revenue,
      formatter: formatCurrency,
      previousValue: previousSummary?.total_revenue ?? null,
    },
    {
      label: 'Pagamentos',
      icon: 'mdi:credit-card-outline',
      value: summary.total_payments,
      formatter: formatNumber,
      previousValue: previousSummary?.total_payments ?? null,
    },
    {
      label: 'Compradores',
      icon: 'mdi:account-group-outline',
      value: summary.total_donors,
      formatter: formatNumber,
      previousValue: previousSummary?.total_donors ?? null,
    },
    {
      label: 'Numeros vendidos',
      icon: 'mdi:numeric',
      value: summary.total_quantity,
      formatter: formatNumber,
      previousValue: previousSummary?.total_quantity ?? null,
    },
    {
      label: 'Ticket medio',
      icon: 'mdi:finance',
      value: summary.average_ticket,
      formatter: formatCurrency,
      previousValue: previousSummary?.average_ticket ?? null,
    },
    {
      label: 'Media por pagamento',
      icon: 'mdi:counter',
      value: summary.average_quantity_per_payment,
      formatter: formatNumber,
      previousValue: previousSummary?.average_quantity_per_payment ?? null,
    },
    {
      label: 'Menor pagamento',
      icon: 'mdi:arrow-collapse-down',
      value: summary.min_payment_amount,
      formatter: formatCurrency,
      previousValue: previousSummary?.min_payment_amount ?? null,
    },
    {
      label: 'Maior pagamento',
      icon: 'mdi:arrow-collapse-up',
      value: summary.max_payment_amount,
      formatter: formatCurrency,
      previousValue: previousSummary?.max_payment_amount ?? null,
    },
  ]

  return (
    <section className={styles.sectionCard}>
      <div className={styles.sectionHeader}>
        <div>
          <h2 className={styles.sectionTitle}>KPIs principais</h2>
          <p className={styles.sectionSubtitle}>
            Indicadores consolidados do periodo selecionado com comparativo da janela
            anterior.
          </p>
        </div>
      </div>

      <div className={styles.kpiGrid}>
        {kpis.map((kpi) => (
          <article key={kpi.label} className={styles.kpiCard}>
            <div className={styles.kpiHeader}>
              <p className={styles.kpiLabel}>{kpi.label}</p>
              <span className={styles.metaPill}>
                <Icon icon={kpi.icon} />
              </span>
              <p className={styles.kpiValue}>{kpi.formatter(kpi.value)}</p>
            </div>
            <div className={styles.kpiMeta}>{renderComparison(kpi.value, kpi.previousValue)}</div>
          </article>
        ))}
      </div>
    </section>
  )
}
